/**
 * 그룹 트리 조립·판정(설계서 §14.20) — React·DOM 을 모르는 순수 함수만 둔다(단위 시험: test/group-tree.test.ts).
 *
 * 서버 `apps/server/src/group/group-tree.util.ts` 와 **같은 규칙**이다. 화면은 미리 막아 헛걸음을 줄이고,
 * 최종 판정은 서버가 한다(둘이 어긋나면 서버 409/400 이 안내한다).
 */
import type { AgentInfo, GroupInfo, OrgEntryInfo } from '../api';

/** 최대 깊이(최상위 = 1) — 서버 `GROUP_TREE_MAX_DEPTH` 와 같은 값이어야 한다. */
export const GROUP_TREE_MAX_DEPTH = 5;

/** 경로 표시 구분자("본사 › 개발팀"). */
export const GROUP_PATH_SEPARATOR = ' › ';

/** 트리 한 마디. 관리 기기·ID 항목은 이 그룹에 **직접** 든 것만(하위는 children 안에). */
export interface GroupNode {
  group: GroupInfo;
  depth: number;
  children: GroupNode[];
  agents: AgentInfo[];
  entries: OrgEntryInfo[];
  /** 이 가지 전체(하위 포함)의 관리 기기 + ID 항목 수 — 접힌 머리에 보인다. */
  total: number;
  /** 이 가지 전체의 온라인 관리 기기 수. */
  online: number;
}

/** 이름 정렬(한글 포함 — 브라우저 로캘 비교). */
const byName = (a: string, b: string) => a.localeCompare(b);

/**
 * 평면 목록 → 트리. 부모가 목록에 없는 그룹(서버가 거른 조상 — 원래 없어야 함)은 최상위로 올린다(사라지지 않게).
 * 기기·항목의 groupId 가 목록에 없으면(경합으로 방금 지워진 그룹 등) 그 항목은 `orphans` 로 돌려 화면이 따로 보이게 한다.
 */
export function buildTree(
  groups: readonly GroupInfo[],
  agents: readonly AgentInfo[],
  entries: readonly OrgEntryInfo[],
): { roots: GroupNode[]; orphans: { agents: AgentInfo[]; entries: OrgEntryInfo[] } } {
  const nodes = new Map<string, GroupNode>();
  for (const g of groups) {
    nodes.set(g.id, { group: g, depth: 0, children: [], agents: [], entries: [], total: 0, online: 0 });
  }
  const roots: GroupNode[] = [];
  for (const n of nodes.values()) {
    const parent = n.group.parentId ? nodes.get(n.group.parentId) : undefined;
    // 자기 자신을 부모로 가리키는 깨진 데이터도 최상위로(무한 루프 방지).
    if (parent && parent !== n) parent.children.push(n);
    else roots.push(n);
  }
  const orphans = { agents: [] as AgentInfo[], entries: [] as OrgEntryInfo[] };
  for (const a of agents) {
    const n = a.groupId ? nodes.get(a.groupId) : undefined;
    if (n) n.agents.push(a);
    else orphans.agents.push(a);
  }
  for (const e of entries) {
    const n = nodes.get(e.groupId);
    if (n) n.entries.push(e);
    else orphans.entries.push(e);
  }
  // 깊이·합계를 위에서 아래로 한 번에(순환 데이터 방어 — 이미 본 마디는 다시 들어가지 않는다).
  const seen = new Set<GroupNode>();
  const walk = (n: GroupNode, depth: number): void => {
    seen.add(n);
    n.depth = depth;
    n.children = n.children.filter((c) => !seen.has(c)).sort((a, b) => byName(a.group.name, b.group.name));
    n.agents.sort((a, b) => byName(a.deviceName || a.deviceCode, b.deviceName || b.deviceCode));
    n.entries.sort((a, b) => byName(a.alias, b.alias));
    n.total = n.agents.length + n.entries.length;
    n.online = n.agents.filter((a) => a.online && !a.disabled).length;
    for (const c of n.children) {
      walk(c, depth + 1);
      n.total += c.total;
      n.online += c.online;
    }
  };
  roots.sort((a, b) => byName(a.group.name, b.group.name));
  for (const r of roots) walk(r, 1);
  return { roots, orphans };
}

/** groupId → 경로 이름("본사 › 개발팀"). 고르기 목록·안내 문구에 쓴다. */
export function groupPath(groups: readonly GroupInfo[], groupId: string): string {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const names: string[] = [];
  const seen = new Set<string>();
  let cur = byId.get(groupId);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    names.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return names.join(GROUP_PATH_SEPARATOR);
}

/** 고르기 목록용 — 트리 순서(부모 다음 자식) + 경로 이름. */
export function groupOptions(groups: readonly GroupInfo[]): { id: string; label: string; depth: number }[] {
  const { roots } = buildTree(groups, [], []);
  const out: { id: string; label: string; depth: number }[] = [];
  const visit = (n: GroupNode) => {
    out.push({ id: n.group.id, label: groupPath(groups, n.group.id), depth: n.depth });
    n.children.forEach(visit);
  };
  roots.forEach(visit);
  return out;
}

/** 깊이(최상위 = 1). 없는 그룹이면 0. */
export function depthOf(groups: readonly GroupInfo[], groupId: string): number {
  const byId = new Map(groups.map((g) => [g.id, g]));
  let depth = 0;
  const seen = new Set<string>();
  let cur = byId.get(groupId);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    depth += 1;
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return depth;
}

/** `candidate` 가 `ancestorId` 자신이거나 그 자손인가. */
export function isSelfOrDescendant(groups: readonly GroupInfo[], ancestorId: string, candidate: string): boolean {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const seen = new Set<string>();
  let cur = byId.get(candidate);
  while (cur && !seen.has(cur.id)) {
    if (cur.id === ancestorId) return true;
    seen.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return false;
}

/** 이 그룹 아래(자기 포함) 가장 깊은 가지의 단 수. */
export function subtreeHeight(groups: readonly GroupInfo[], groupId: string): number {
  const kids = new Map<string, string[]>();
  for (const g of groups) {
    if (g.parentId) kids.set(g.parentId, [...(kids.get(g.parentId) ?? []), g.id]);
  }
  const seen = new Set<string>();
  const h = (id: string): number => {
    if (seen.has(id)) return 0;
    seen.add(id);
    const k = kids.get(id) ?? [];
    return 1 + (k.length ? Math.max(...k.map(h)) : 0);
  };
  return h(groupId);
}

/** 하위 그룹을 더 만들 수 있는가(깊이 상한). */
export function canAddChild(groups: readonly GroupInfo[], parentId: string): boolean {
  return depthOf(groups, parentId) < GROUP_TREE_MAX_DEPTH;
}

/**
 * 그룹을 `newParentId` 아래로 옮길 수 있는가 — 안 되면 사람이 읽을 이유, 되면 null.
 * @param newParentId null = 최상위로
 */
export function groupMoveProblem(groups: readonly GroupInfo[], groupId: string, newParentId: string | null): string | null {
  const g = groups.find((x) => x.id === groupId);
  if (!g) return '그룹을 찾을 수 없습니다.';
  if (g.parentId === newParentId) return '이미 그 위치에 있습니다.';
  if (newParentId && isSelfOrDescendant(groups, groupId, newParentId)) {
    return '자기 자신이나 하위 그룹 아래로는 옮길 수 없습니다.';
  }
  const parentDepth = newParentId ? depthOf(groups, newParentId) : 0;
  if (parentDepth + subtreeHeight(groups, groupId) > GROUP_TREE_MAX_DEPTH) {
    return `옮기면 ${GROUP_TREE_MAX_DEPTH}단계를 넘습니다.`;
  }
  if (groups.some((x) => x.id !== groupId && x.parentId === newParentId && x.name === g.name)) {
    return '옮길 위치에 같은 이름의 그룹이 있습니다.';
  }
  return null;
}

/**
 * 그룹을 지울 수 있는가 — 서버와 같은 규칙(비었을 때만 · 마지막 그룹 불가). 안 되면 이유.
 * 일반 사용자 목록은 서버가 걸러서 오므로 이 판정은 관리자 화면에서만 의미가 있다.
 */
export function groupDeleteProblem(groups: readonly GroupInfo[], groupId: string): string | null {
  const g = groups.find((x) => x.id === groupId);
  if (!g) return '그룹을 찾을 수 없습니다.';
  if (g.deviceCount + g.entryCount + g.childCount > 0) {
    return '비어 있는 그룹만 삭제할 수 있습니다. 기기·하위 그룹을 먼저 옮기세요.';
  }
  if (groups.length <= 1) return '조직에는 그룹이 하나 이상 있어야 합니다.';
  return null;
}

// ── 펼침 상태(그룹별 기억) ─────────────────────────────────────────────────────────────

/** localStorage 키 — 조직마다 따로(수퍼유저가 여러 조직을 오간다). */
export const EXPAND_STORAGE_PREFIX = 'rs.groupTree.collapsed.';

/** 접힌 그룹 id 집합을 읽는다(기본 = 모두 펼침). 저장소가 막혀도 빈 집합. */
export function loadCollapsed(storage: Pick<Storage, 'getItem'> | null, scope: string): Set<string> {
  try {
    const raw = storage?.getItem(EXPAND_STORAGE_PREFIX + scope);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/** 접힌 그룹 id 집합을 저장한다(실패는 조용히 — 편의 기능이다). */
export function saveCollapsed(storage: Pick<Storage, 'setItem'> | null, scope: string, collapsed: Set<string>): void {
  try {
    storage?.setItem(EXPAND_STORAGE_PREFIX + scope, JSON.stringify([...collapsed]));
  } catch {
    /* 사생활 모드·저장소 차단 — 무시 */
  }
}
